import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { ApplicationInsights, IAppInsightsCore, IConfiguration, IProcessTelemetryContext, ITelemetryItem, ITelemetryPlugin } from '@microsoft/applicationinsights-web';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { environment } from 'src/environments/environment';
import { DeviceInfoService } from '../../services/device-info.service';
import { AppUserService } from '../../services/app-user.service';

@Injectable({
  providedIn: 'root',
})
export class ApplicationInsightsService {
  private appInsights: ApplicationInsights;
  private customMetadataPlugin: CustomMetadataPlugin;
  private inputType: string;
  private userId: number;

  constructor(
    private router: Router,
    private deviceInfoService: DeviceInfoService,
    private appUserService: AppUserService
  ) {
    this.initializeAppInsights();

    this.deviceInfoService.getInputType().then(inputType => {
      this.inputType = inputType;
      this.customMetadataPlugin.setInputType(this.inputType);
    });

    this.appUserService.currentUserId$.subscribe(id => this.setUserId(id));
  }

  private setUserId(id: number | null) {
    this.userId = id ?? null;
    if (id) {
      const userIdStr = id.toString();
      this.customMetadataPlugin.setUserId(userIdStr);
      this.appInsights.setAuthenticatedUserContext(userIdStr, userIdStr, true);
    } else {
      this.customMetadataPlugin.setUserId(null);
      this.appInsights.clearAuthenticatedUserContext();
    }
  }

  private initializeAppInsights() {
    this.customMetadataPlugin = new CustomMetadataPlugin();
    const angularPlugin = new AngularPlugin();

    this.appInsights = new ApplicationInsights({
      config: {
        connectionString: environment.applicationInsightsConnectionString,
        enableAutoRouteTracking: true,
        enableRequestHeaderTracking: true,   // injects trace headers so that backend can correlate requests
        enableResponseHeaderTracking: true,
        disableFetchTracking: true,
        disableAjaxTracking: false,
        isBeaconApiDisabled: false,   // allow Beacon for background/low-overhead sending
        maxBatchInterval: 20000,      // batch telemetry for up to 20s
        maxBatchSizeInBytes: 10000,   // small batches for constrained devices
        enableUnhandledPromiseRejectionTracking: true, // capture async errors
        isRetryDisabled: false,       // retry failed sends when network returns
        bufferOverride: {
          getItem: (logger, key) => localStorage.getItem(key),
          setItem: (logger, key, value) => {
            localStorage.setItem(key, value);
            return true;
          },
        },
        extensions: [this.customMetadataPlugin, angularPlugin],
        extensionConfig: {
          [angularPlugin.identifier]: { router: this.router },
        },
        enableCorsCorrelation: true,
      },
    });

    this.appInsights.addTelemetryInitializer(event => {
      if (event.baseType?.includes('Pageview')) {
        return false;
      }

      if (event.baseType === 'RemoteDependencyData') {
        const data = event.baseData as any;
        const url: string = data?.target || data?.name || '';

        if (url.includes('/svg/') ||
          url.includes('/assets/') ||
          url.includes('.css') ||
          url.includes('.js')
        ) {
          return false;
        }
      }

      return true;
    });

    this.appInsights.loadAppInsights();
  }

  logPageView(name?: string, url?: string) {
    this.appInsights.trackPageView({
      name: name,
      uri: url,
    });
  }

  logEvent(name: string, properties?: { [key: string]: any }) {
    this.appInsights.trackEvent({ name: name, properties: { ...properties, UserId: this.userId, InputType: this.inputType } });
  }

  logMetric(name: string, average: number, properties?: { [key: string]: any }) {
    this.appInsights.trackMetric({ name: name, average: average }, { ...properties, UserId: this.userId, InputType: this.inputType });
  }

  logException(exception: Error, properties?: { [key: string]: any }, severityLevel?: number) {
    this.appInsights.trackException({ exception: exception, severityLevel: severityLevel, properties: { ...properties, UserId: this.userId, InputType: this.inputType } });
  }

  logTrace(message: string, properties?: { [key: string]: any }) {
    this.appInsights.trackTrace({ message: message }, { ...properties, UserId: this.userId, InputType: this.inputType });
  }

  flush() {
    this.appInsights.flush();
  }

  startDiagnosticsSession(id: string) {
    this.customMetadataPlugin.setDiagSessionId(id);
  }

  stopDiagnosticsSession() {
    this.customMetadataPlugin.setDiagSessionId(undefined);
  }

  /**
   * Exposes the current telemetry trace context (traceId & parentId/spanId) for correlation headers.
   */
  public getTelemetryTrace() {
    return this.appInsights?.context?.telemetryTrace;
  }

  public generateSpanId(): string {
    return [...crypto.getRandomValues(new Uint8Array(8))]
      .map(b => b.toString(16).padStart(2, "0"))
      .join("");
  }
}

class CustomMetadataPlugin implements ITelemetryPlugin {
  identifier: string = 'CustomMetadataPlugin';
  priority: number = 300;
  private nextPlugin?: ITelemetryPlugin;
  private userId?: string;
  private inputType: string;
  private debugSessionId?: string;

  public initialize(config: IConfiguration, core: IAppInsightsCore, extensions: ITelemetryPlugin[]): void { }

  public processTelemetry(env: ITelemetryItem, itemCtx?: IProcessTelemetryContext): void {
    const props = (env.baseData as any).properties ?? {};
    props['UserId'] = this.userId;
    props['InputType'] = this.inputType;
    if (this.debugSessionId) props['DebugSessionId'] = this.debugSessionId;
    (env.baseData as any).properties = props;

    this.nextPlugin?.processTelemetry(env, itemCtx);
  }

  public setNextPlugin(next: ITelemetryPlugin): void {
    this.nextPlugin = next;
  }

  public setUserId(userId: string | null) {
    this.userId = userId;
  }

  public setInputType(inputType: string) {
    this.inputType = inputType;
  }

  public setDiagSessionId(id?: string) {
    this.debugSessionId = id;
  }
}
